import { useMemo } from 'react';
import {
    classifyPassengerServices,
    passengerMatchesRewardTier,
} from '../helpers/passengerServiceNormalizer';
import { passengerMatchesSpecialNeedsCategories } from '../helpers/ssrCategories';
import { passengerHasRewardBenefit } from '../helpers/rewardTier';

export const usePassengerFiltering = (
    passengerList,
    selectedFilter,
    getBagInfo,
    hasLoadedBagData = false
) => {
    return useMemo(() => {
        if (!Array.isArray(passengerList)) {
            return [];
        }

        let filtered = [...passengerList];

        // Filter by check-in status
        if (selectedFilter.statusFilter === 'CHECKED_IN') {
            filtered = filtered.filter((p) => p.checkedIn);
        } else if (selectedFilter.statusFilter === 'NOT_CHECKED_IN') {
            filtered = filtered.filter((p) => !p.checkedIn);
        }

        // Collect all active service filters
        // Use AND logic when multiple service filters are active
        const serviceFilters = [];

        if (selectedFilter.SPECIAL_NEEDS) {
            const categories = selectedFilter.specialNeedsCategories || [];
            serviceFilters.push((p) =>
                passengerMatchesSpecialNeedsCategories(p, categories)
            );
        }


        if (selectedFilter.ID_STAFF) {
            serviceFilters.push((p) => {
                const classification = classifyPassengerServices(p);
                return classification.hasIdStaff;
            });
        }

        if (selectedFilter.OTHER_INFO) {
            serviceFilters.push((p) => {
                const classification = classifyPassengerServices(p);
                return classification.hasOtherInfo;
            });
        }

        if (serviceFilters.length > 0) {
            filtered = filtered.filter((p) =>
                serviceFilters.every((filterFn) => filterFn(p))
            );
        }

        const selectedRewardTiers = selectedFilter.rewardTiers || [];
        if (selectedRewardTiers.length > 0) {
            filtered = filtered.filter((p) =>
                selectedRewardTiers.some((tierKey) =>
                    passengerMatchesRewardTier(p, tierKey)
                )
            );
        }

        const selectedRewardBenefits = selectedFilter.rewardBenefits || [];
        if (selectedRewardBenefits.length > 0) {
            filtered = filtered.filter((p) =>
                selectedRewardBenefits.every((benefitKey) =>
                    passengerHasRewardBenefit(p, benefitKey)
                )
            );
        }

        // Filter connections (can be combined with other filters)
        if (
            selectedFilter.inboundConnections ||
            selectedFilter.outboundConnections
        ) {
            filtered = filtered.filter((p) => {
                const hasInbound = p.inboundConnections?.length > 0;
                const hasOutbound = p.outboundConnections?.length > 0;

                if (
                    selectedFilter.inboundConnections &&
                    selectedFilter.outboundConnections
                ) {
                    // AND logic when both are selected
                    return hasInbound && hasOutbound;
                } else if (selectedFilter.inboundConnections) {
                    return hasInbound;
                } else if (selectedFilter.outboundConnections) {
                    return hasOutbound;
                }
                return true;
            });
        }

        // Filter by baggage status
        const applyLoadedFilter = selectedFilter.BAGS_LOADED && hasLoadedBagData;
        if (selectedFilter.BAGS_CHECKED_IN || applyLoadedFilter) {
            filtered = filtered.filter((p) => {
                const bagInfo =
                    typeof getBagInfo === 'function' ? getBagInfo(p) : null;

                if (
                    selectedFilter.BAGS_CHECKED_IN &&
                    !(bagInfo?.checkedInCount > 0)
                ) {
                    return false;
                }

                if (applyLoadedFilter && !(bagInfo?.loadedCount > 0)) {
                    return false;
                }

                return true;
            });
        }

        // Filter by meal categories
        const selectedMeals = selectedFilter.selectedMeals || [];
        if (selectedMeals.includes('All')) {
            filtered = filtered.filter((p) => p.mealObject);
        } else if (selectedMeals.length > 0) {
            filtered = filtered.filter((p) => {
                const category = p?.mealObject?.category;
                return p.mealObject && category && selectedMeals.includes(category);
            });
        }

        return filtered;
    }, [passengerList, selectedFilter, getBagInfo, hasLoadedBagData]);
};
